import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RatingBadge } from "@/components/ReviewsSection";
import { ResolutionThread } from "@/pages/MyBookings";

const ReplyBox = ({ review, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/reviews/${review.id}/reply`, { text });
      toast.success("Reply posted");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex gap-2">
      <Textarea rows={1} data-testid={`reply-input-${review.id}`} className="min-h-[40px]" placeholder="Reply publicly…"
        value={text} onChange={(e) => setText(e.target.value)} />
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`reply-send-${review.id}`} disabled={busy || !text.trim()} onClick={send}>Reply</Button>
    </div>
  );
};

export const ReviewsPanel = () => {
  const [held, setHeld] = useState([]);
  const [published, setPublished] = useState([]);

  const load = useCallback(() => {
    api.get("/reviews/held-for-me").then((r) => setHeld(r.data));
    api.get("/reviews/published-for-me").then((r) => setPublished(r.data));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="space-y-12" data-testid="practitioner-reviews-panel">
      <section>
        <h3 className="font-display text-lg md:text-2xl text-foreground mb-2">Private resolutions</h3>
        <p className="text-sm text-muted-foreground mb-5">1-star reviews are held from public view while you resolve them privately with the seeker. Unresolved after 7 days, they escalate to the O.R.E.C team.</p>
        {held.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="held-reviews-empty">Nothing on hold — lovely.</p>
        ) : (
          <div className="space-y-4">
            {held.map((r) => (
              <div key={r.id} className="silk-card rounded-lg p-5" data-testid={`held-review-${r.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-foreground">{r.seeker_name} · {r.service_name}</p>
                  <span className={`text-xs px-3 py-1 rounded-full border ${r.status === "escalated" ? "bg-muted text-muted-foreground border-border" : "bg-foreground/10 text-foreground border-foreground/30"}`}>
                    {r.status === "escalated" ? "Escalated to admin" : "On hold"}
                  </span>
                </div>
                {r.comment && <p className="text-sm text-muted-foreground mb-4">"{r.comment}"</p>}
                <ResolutionThread review={r} canResolve={false} onChanged={load} />
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="font-display text-lg md:text-2xl text-foreground mb-5">Published reviews</h3>
        {published.length === 0 ? (
          <p className="text-sm text-muted-foreground">No public reviews yet.</p>
        ) : (
          <div className="space-y-4">
            {published.map((r) => (
              <div key={r.id} className="silk-card rounded-lg p-5" data-testid={`published-review-${r.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-foreground">{r.seeker_name} · {r.service_name}</p>
                  <RatingBadge rating={r.rating} />
                </div>
                {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                {r.reply?.text ? (
                  <p className="text-xs text-muted-foreground mt-3 ml-4 border-l-2 border-primary/30 pl-3">Your reply: {r.reply.text}</p>
                ) : (
                  <ReplyBox review={r} onDone={load} />
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
